import { FormControl, FormErrorMessage, Flex } from "@chakra-ui/react";
import { FormProvider, useForm } from "react-hook-form";
import Input from "../inputs/Input";
import { forwardRef, useImperativeHandle } from "react";
import type { Earmold } from "../../types/earmold.types";
import Select from "../inputs/Select";
import FileImageInput from "../FileImageInput";
import { useFile } from "../../hooks/file.hooks";

type EarmoldFormProps = {
	value?: Earmold | null;
};

export type EarmoldFormHandler = {
	values: () => Partial<Earmold>;
	valid: () => Promise<boolean>;
};

const EarmoldForm = forwardRef<EarmoldFormHandler, EarmoldFormProps>(
	({ value }: EarmoldFormProps, ref) => {
		const methods = useForm();
		const { data: file } = useFile({ id: value?.fileId });

		useImperativeHandle(ref, () => ({
			valid: () => {
				return methods.trigger();
			},
			values: () => {
				const values = methods.getValues();

				return {
					label: values.label,
					description: values.description || null,
					price: values.price * 100,
					sageId: values.sageId,
					status: values.status,
					category: values.category,
					file: values.file,
				};
			},
		}));
		return (
			<FormProvider {...methods}>
				<form style={{ marginTop: "32px", marginBottom: "32px" }}>
					<Flex direction="column" gap="24px">
						<FormControl
							isInvalid={!!methods.formState.errors.file}
							className="flex justify-center"
						>
							<div className="flex flex-col items-center">
								<FileImageInput
									defaultValue={file}
									onFileChange={(files: File[] | null) => {
										if (!files || files.length === 0) {
											methods.setValue("file", null);
											return;
										}

										methods.setValue("file", files[0]);
									}}
									{...methods.register("file", {
										required: "Ce champ est obligatoire",
									})}
								></FileImageInput>
								<FormErrorMessage>
									<>{methods.formState.errors?.file?.message}</>
								</FormErrorMessage>
							</div>
						</FormControl>
						<FormControl isInvalid={!!methods.formState.errors.label}>
							<Input
								id="label"
								placeholder="Nom"
								defaultValue={value?.label ?? ""}
								{...methods.register("label", {
									required: "Ce champ est obligatoire",
								})}
							></Input>
							<FormErrorMessage>
								<>{methods.formState.errors?.label?.message}</>
							</FormErrorMessage>
						</FormControl>

						<div className="grid [grid-template-columns:3fr_1fr] gap-4">
							<FormControl isInvalid={!!methods.formState.errors.sageId}>
								<Input
									id="sageId"
									placeholder="ID Sage"
									defaultValue={value?.sageId ?? ""}
									{...methods.register("sageId", {
										required: "Ce champ est obligatoire",
									})}
								></Input>
								<FormErrorMessage>
									<>{methods.formState.errors?.sageId?.message}</>
								</FormErrorMessage>
							</FormControl>

							<FormControl isInvalid={!!methods.formState.errors.price}>
								<Input
									id="price"
									placeholder="Prix (€)"
									type="number"
									defaultValue={value?.price ? value.price / 100 : ""}
									{...methods.register("price", {
										required: "Ce champ est obligatoire",
									})}
								></Input>
								<FormErrorMessage>
									<>{methods.formState.errors?.price?.message}</>
								</FormErrorMessage>
							</FormControl>
						</div>

						<FormControl isInvalid={!!methods.formState.errors.category}>
							<Select
								id="category"
								placeholder="Statut"
								defaultValue={value?.category ?? "HEARING_AID"}
								{...methods.register("category", {
									required: "Ce champ est obligatoire",
								})}
							>
								<option value="HEARING_AID">Embouts appareils auditifs</option>
								<option value="PROTECTION">Protections</option>
								<option value="COMM_AND_MONITORING">
									Communication & monitoring
								</option>
								<option value="OTHER">Autre</option>
							</Select>
							<FormErrorMessage>
								<>{methods.formState.errors?.category?.message}</>
							</FormErrorMessage>
						</FormControl>

						<FormControl isInvalid={!!methods.formState.errors.status}>
							<Select
								id="status"
								placeholder="Statut"
								defaultValue={value?.status ?? "DRAFT"}
								{...methods.register("status", {
									required: "Ce champ est obligatoire",
								})}
							>
								<option value="DRAFT">Brouillon</option>
								<option value="ACTIVE">Actif</option>
							</Select>
							<FormErrorMessage>
								<>{methods.formState.errors?.status?.message}</>
							</FormErrorMessage>
						</FormControl>

						<FormControl isInvalid={!!methods.formState.errors.label}>
							<Input
								id="description"
								placeholder="Texte explicatif (facultatif)"
								defaultValue={value?.description ?? ""}
								{...methods.register("description")}
							></Input>
							<FormErrorMessage>
								<>{methods.formState.errors?.description?.message}</>
							</FormErrorMessage>
						</FormControl>
					</Flex>
				</form>
			</FormProvider>
		);
	},
);

export default EarmoldForm;
